import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

const BannerAdmin = () => {
  const [bannerData, setBannerData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form state
  const [formData, setFormData] = useState({
    subTitle: "",
    mainTitle: "",
    highlightText: "",
    description: "",
    buttonText: "",
    buttonLink: "",
    youtubeUrl: "",
    backgroundImage: "",
    isActive: true,
  });

  // Fetch banner data
  useEffect(() => {
    const fetchBannerData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch("https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/banner-section");

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setBannerData(result.data);
          setFormData({
            subTitle: result.data.subTitle || "",
            mainTitle: result.data.mainTitle || "",
            highlightText: result.data.highlightText || "",
            description: result.data.description || "",
            buttonText: result.data.buttonText || "",
            buttonLink: result.data.buttonLink || "",
            youtubeUrl: result.data.youtubeUrl || "",
            backgroundImage: result.data.backgroundImage || "",
            isActive:
              result.data.isActive !== undefined ? result.data.isActive : true,
          });
        } else {
          setError(result.message || "Failed to fetch banner data");
        }
      } catch (err) {
        console.error("Error fetching banner data:", err);
        setError("Network error or server not responding");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBannerData();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const response = await fetch("https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/banner-section", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        toast.success("Banner updated successfully!");
        setBannerData(result.data);
      } else {
        toast.error(result.message || "Failed to update banner");
      }
    } catch (err) {
      console.error("Error updating banner:", err);
      toast.error("Network error or server not responding");
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle banner active status
  const handleToggleActive = async () => {
    try {
      const response = await fetch("https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/banner-section/toggle", {
        method: "PATCH",
      });

      const result = await response.json();

      if (result.success) {
        toast.success(
          `Banner ${
            result.data.isActive ? "activated" : "deactivated"
          } successfully!`
        );
        setBannerData(result.data);
        setFormData((prev) => ({ ...prev, isActive: result.data.isActive }));
      } else {
        toast.error(result.message || "Failed to toggle banner status");
      }
    } catch (err) {
      console.error("Error toggling banner status:", err);
      toast.error("Network error or server not responding");
    }
  };

  // Reset banner to default
  const handleResetToDefault = async () => {
    if (
      !window.confirm(
        "Are you sure you want to reset the banner to default settings?"
      )
    ) {
      return;
    }

    try {
      const response = await fetch("https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/banner-section/reset", {
        method: "POST",
      });

      const result = await response.json();

      if (result.success) {
        toast.success("Banner reset to default successfully!");
        setBannerData(result.data);
        setFormData({
          subTitle: result.data.subTitle || "",
          mainTitle: result.data.mainTitle || "",
          highlightText: result.data.highlightText || "",
          description: result.data.description || "",
          buttonText: result.data.buttonText || "",
          buttonLink: result.data.buttonLink || "",
          youtubeUrl: result.data.youtubeUrl || "",
          backgroundImage: result.data.backgroundImage || "",
          isActive:
            result.data.isActive !== undefined ? result.data.isActive : true,
        });
      } else {
        toast.error(result.message || "Failed to reset banner");
      }
    } catch (err) {
      console.error("Error resetting banner:", err);
      toast.error("Network error or server not responding");
    }
  };

  // Deactivate banner
  const handleDeactivate = async () => {
    if (!window.confirm("Are you sure you want to deactivate the banner?")) {
      return;
    }

    try {
      const response = await fetch("https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/banner-section", {
        method: "DELETE",
      });

      const result = await response.json();

      if (result.success) {
        toast.success("Banner deactivated successfully!");
        setBannerData((prev) => ({ ...prev, isActive: false }));
        setFormData((prev) => ({ ...prev, isActive: false }));
      } else {
        toast.error(result.message || "Failed to deactivate banner");
      }
    } catch (err) {
      console.error("Error deactivating banner:", err);
      toast.error("Network error or server not responding");
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading banner data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4>Error Loading Banner Data</h4>
        <p>{error}</p>
        <button
          className="btn btn-outline-danger"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="banner-admin">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Banner Section Management</h2>
        <div>
          <button
            className={`btn ${
              bannerData?.isActive ? "btn-warning" : "btn-success"
            } me-2`}
            onClick={handleToggleActive}
          >
            {bannerData?.isActive ? "Deactivate" : "Activate"} Banner
          </button>
          <button
            className="btn btn-danger me-2"
            onClick={handleDeactivate}
            disabled={!bannerData?.isActive}
          >
            Delete Banner
          </button>
          <button className="btn btn-secondary" onClick={handleResetToDefault}>
            Reset to Default
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="subTitle" className="form-label">
                  Subtitle
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="subTitle"
                  name="subTitle"
                  value={formData.subTitle}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="mainTitle" className="form-label">
                  Main Title
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="mainTitle"
                  name="mainTitle"
                  value={formData.mainTitle}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="highlightText" className="form-label">
                  Highlight Text
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="highlightText"
                  name="highlightText"
                  value={formData.highlightText}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="buttonText" className="form-label">
                  Button Text
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="buttonText"
                  name="buttonText"
                  value={formData.buttonText}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="buttonLink" className="form-label">
                  Button Link
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="buttonLink"
                  name="buttonLink"
                  value={formData.buttonLink}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="youtubeUrl" className="form-label">
                  YouTube URL
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="youtubeUrl"
                  name="youtubeUrl"
                  value={formData.youtubeUrl}
                  onChange={handleInputChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <div className="form-text">
                  Enter a YouTube URL to display a video in the banner
                </div>
              </div>

              <div className="col-md-12 mb-3">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <textarea
                  className="form-control"
                  id="description"
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={handleInputChange}
                ></textarea>
              </div>

              {/* <div className="col-md-12 mb-3">
                <label htmlFor="backgroundImage" className="form-label">
                  Background Image URL
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="backgroundImage"
                  name="backgroundImage"
                  value={formData.backgroundImage}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.jpg"
                />
                <div className="form-text">
                  Enter a URL for a background image (JPG, PNG, GIF, WebP)
                </div>
              </div> */}

              <div className="col-12 mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Banner is active
                  </label>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {bannerData && (
        <div className="card mt-4">
          <div className="card-header">
            <h5>Current Banner Status</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={`badge ${
                      bannerData.isActive ? "bg-success" : "bg-danger"
                    }`}
                  >
                    {bannerData.isActive ? "Active" : "Inactive"}
                  </span>
                </p>
                <p>
                  <strong>Last Updated:</strong>{" "}
                  {new Date(bannerData.updatedAt).toLocaleString()}
                </p>
                <p>
                  <strong>Created:</strong>{" "}
                  {new Date(bannerData.createdAt).toLocaleString()}
                </p>
              </div>
              <div className="col-md-6">
                <p>
                  <strong>Has Video:</strong>{" "}
                  <span
                    className={`badge ${
                      bannerData.youtubeUrl ? "bg-success" : "bg-secondary"
                    }`}
                  >
                    {bannerData.youtubeUrl ? "Yes" : "No"}
                  </span>
                </p>
                <p>
                  <strong>Has Background:</strong>{" "}
                  <span
                    className={`badge ${
                      bannerData.backgroundImage ? "bg-success" : "bg-secondary"
                    }`}
                  >
                    {bannerData.backgroundImage ? "Yes" : "No"}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerAdmin;
